/**
 * Realtime collab client — Socket.IO wrapper.
 *
 * Mirrors the backend conventions in `backend/app/realtime/`:
 *
 * - Connects to the `/dashboard` namespace using a Logto access token as
 *   the auth.token field. The backend verifies via `_decode_logto_token`
 *   on `on_connect` — invalid tokens raise `ConnectionRefusedError`
 *   which surfaces here as `connect_error`.
 * - Event names match the namespace handlers exactly (`doc:join`,
 *   `doc:field_save`, `doc:cursor`, `doc:presence:joined`, etc.).
 *
 * The feature flag (`VITE_FEATURE_REALTIME_COLLAB_V1`) is checked at the
 * call sites in `useDocumentCollab` — this module is a thin wrapper that
 * doesn't gate itself, so unit tests can drive it without env shims.
 *
 * NOTE on session resume: the backend `presence:hello` payload still
 * includes `session_id` / `last_seq` / `replay`, but this client does
 * not consume them. The `useDocumentCollab` hook re-issues `doc:join`
 * on every `connect` event, so doc-room presence is rebuilt from
 * scratch after a reconnect rather than replayed from the server's
 * buffer. Wiring resume-tokens here without a consumer that advances
 * `last_seq` and replays buffered events would cause every reconnect
 * to replay the entire buffer from `last_seq=0` — worse than no
 * resume at all. Re-introduce the plumbing only alongside an event
 * stream that actually carries `seq` and a handler that bumps it.
 */
import { io, type Socket } from 'socket.io-client'

import { API_BASE_URL } from '@/api/client'

export type CollabConnectionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error'

export interface PresenceParticipant {
  session_id: string
  user_id: string
  display: string
}

export interface DocPresenceJoinedPayload {
  doc_uuid: string
  joined: PresenceParticipant
  presence: PresenceParticipant[]
}

export interface DocPresenceLeftPayload {
  doc_uuid: string
  session_id: string
  user_id: string
  display: string
  presence: PresenceParticipant[]
}

export interface DocFieldChangedPayload {
  doc_uuid: string
  field_uuid: string
  current_value: string | null
  current_updated_at: string | null
  by: { session_id: string; user_id: string }
}

export interface DocCursorPayload {
  doc_uuid: string
  field_uuid: string | null
  session_id: string
  user_id: string
}

export interface DocJoinAck {
  ok: boolean
  reason?: string
  self?: PresenceParticipant
  presence?: PresenceParticipant[]
}

export interface DocFieldSaveAck {
  ok: boolean
  reason?: string
  field_uuid?: string
  current_value?: string | null
  current_updated_at?: string | null
}

export interface CollabClientOptions {
  baseUrl?: string
  // Resolves a fresh Logto access token. Each (re)connect attempt calls
  // this so a stale token doesn't pin the socket to an expired bearer.
  getAccessToken: () => Promise<string>
}

/**
 * Light wrapper around `Socket` that exposes the dashboard namespace
 * conventions in a strongly-typed way.
 *
 * Construct one per page (or one per logged-in user); call `dispose()`
 * on unmount.
 */
export class CollabClient {
  private socket: Socket | null = null
  private readonly baseUrl: string
  private readonly getAccessToken: () => Promise<string>

  constructor(options: CollabClientOptions) {
    this.baseUrl = options.baseUrl ?? API_BASE_URL
    this.getAccessToken = options.getAccessToken
  }

  /**
   * Lazily create the underlying socket. `auth` is a function so the
   * socket.io client refetches a fresh access token on every
   * (re)connect — matching the cache-invalidation pattern `authFetch`
   * uses to handle Logto-expired bearers.
   */
  connect(): Socket {
    if (this.socket) return this.socket
    const socket = io(`${this.baseUrl}/dashboard`, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
      randomizationFactor: 0.4,
      auth: async (cb: (data: { token: string }) => void) => {
        const token = await this.getAccessToken().catch(() => '')
        cb({ token })
      },
    })
    this.socket = socket
    return socket
  }

  /** Cleanly shut down. Idempotent. */
  dispose(): void {
    if (!this.socket) return
    this.socket.removeAllListeners()
    this.socket.disconnect()
    this.socket = null
  }
}

/** Read the feature flag at call time so tests can flip it with envset. */
export function isRealtimeCollabFeatureEnabled(): boolean {
  const raw =
    import.meta.env.VITE_FEATURE_REALTIME_COLLAB_V1 ??
    import.meta.env.VITE_FEATURE_REALTIME_COLLAB
  return raw === '1' || raw === 'true' || raw === 'on'
}
